import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { LeadFunnelStage } from '../../types/franchise';

interface LeadConversionFunnelProps {
  data: LeadFunnelStage[];
}

export const LeadConversionFunnel: React.FC<LeadConversionFunnelProps> = ({ data }) => {
  const maxCount = data[0]?.count || 1;

  // Calculate conversion rates between stages
  const conversionRates = data.map((stage, i) => {
    if (i === 0) return null;
    const prevCount = data[i - 1].count;
    return prevCount > 0 ? Math.round((stage.count / prevCount) * 100) : 0;
  });

  // Overall conversion
  const overallConversion = data.length >= 2 && data[0]?.count > 0
    ? Math.round((data[data.length - 1].count / data[0].count) * 100)
    : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Lead Conversion Funnel
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#2E7D32' }}>
              {overallConversion}%
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Overall conversion
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {data.map((stage, index) => {
            const widthPercent = Math.max((stage.count / maxCount) * 100, 30);

            return (
              <Box key={stage.stage}>
                {/* Conversion rate arrow between stages */}
                {index > 0 && conversionRates[index] !== null && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: 'text.secondary',
                        bgcolor: '#f5f5f5',
                        px: 1.5,
                        py: 0.25,
                        borderRadius: '10px',
                      }}
                    >
                      {conversionRates[index]}% conversion
                    </Typography>
                  </Box>
                )}

                {/* Funnel bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${widthPercent}%`,
                        minWidth: 120,
                        background: `linear-gradient(135deg, ${stage.color} 0%, ${stage.color}CC 100%)`,
                        borderRadius: '8px',
                        py: 1.5,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: `0 4px 16px ${stage.color}40`,
                        },
                      }}
                    >
                      <Typography sx={{ color: 'white', fontSize: '0.82rem', fontWeight: 600 }}>
                        {stage.stage}
                      </Typography>
                      <Typography sx={{ color: 'white', fontSize: '1.1rem', fontWeight: 800 }}>
                        {stage.count}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};
